import {ActionIcon, createStyles, Group, Select, Stack, Text, Title} from '@mantine/core';
import React, {useEffect, useRef} from 'react';

import {countResolvedSourceOptions} from 'antibodies/schema-configuration/schema-migration/newFieldsMigrationUtils';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

// Sentinel value for the explicit "— none —" select item, distinct from both a real allowed-option
// name and an implicitly-unmapped option (which has no entry in the map and shows the placeholder
// instead). A leading null char is used because a real option name can never contain one, so this
// can never collide with a legitimate value.
const NONE_OPTION_VALUE = '\0none';

const useStyles = createStyles((theme) => ({
  // Extra breathing room around the mapping table so it isn't flush against the header/modal edges.
  table: {
    paddingLeft: theme.spacing.md,
    paddingRight: theme.spacing.md,
    paddingBottom: theme.spacing.md,
  },
  // Fixed-width left column so the destination selects all start at the same x-position regardless
  // of how long each source option name is.
  sourceName: {
    width: '40%',
    flexShrink: 0,
  },
  destSelect: {
    flex: 1,
    minWidth: 0,
  },
  columnHeaders: {
    color: theme.colors.gray[7],
    fontWeight: 600,
  },
  // Matches sourceName's width so the "Source field values" header sits above the source names.
  sourceHeader: {
    width: '40%',
  },
  // Subtle separator between rows for a spacious, scannable table; the last row omits it so there's
  // no trailing rule above the modal's bottom padding.
  row: {
    paddingTop: theme.spacing.sm,
    paddingBottom: theme.spacing.sm,
    borderBottom: `1px solid ${theme.colors.gray[2]}`,
    '&:last-of-type': {
      borderBottom: 'none',
    },
  },
}));

export interface SourceFieldValueMappingViewProps {
  destinationFieldName: string;
  allowedOptions: Array<string>;
  sourceOptions: Array<{id: string; name: string}>;
  // Keyed by source option id. A present key (even with a null value = explicit "— none —") means the
  // option has been acknowledged; an absent key means it is still implicitly unmapped.
  currentMappings: Map<string, string | null>;
  onMappingChange: (sourceOptionId: string, destOptionName: string | null) => void;
  onBack: () => void;
}

/**
 * Drill-in sub-view for mapping a source field's option values onto a new field's allowed options.
 * Replaces the older inline option-mapping panel: it takes over the modal body while active, and the
 * back chevron is the only way out. Changes are applied live via onMappingChange (there is no
 * separate Confirm/Cancel step).
 */
export default function SourceFieldValueMappingView({
  destinationFieldName,
  allowedOptions,
  sourceOptions,
  currentMappings,
  onMappingChange,
  onBack,
}: SourceFieldValueMappingViewProps): JSX.Element {
  const {classes} = useStyles();
  // Move focus onto the Back button when the sub-view takes over the modal body. Swapping panes
  // unmounts the caret that had focus without reinitializing the still-mounted Modal's focus trap, so
  // focus would otherwise fall to document.body — stranding keyboard and screen-reader users.
  const backButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    backButtonRef.current?.focus();
  }, []);
  const destSelectData = [
    {value: NONE_OPTION_VALUE, label: '— none —'},
    ...allowedOptions.map((name) => ({value: name, label: name})),
  ];
  const resolvedCount = countResolvedSourceOptions(
    sourceOptions,
    Array.from(currentMappings, ([sourceOptionId, destinationOptionName]) => ({
      sourceOptionId,
      destinationOptionName,
    })),
    allowedOptions
  );

  return (
    <Stack spacing="lg">
      <Group spacing="xs" align="center" noWrap={true}>
        <ActionIcon
          ref={backButtonRef}
          size="sm"
          aria-label="Back to preview"
          variant="subtle"
          onClick={onBack}
        >
          <BenchlingIcon icon="chevron-left" />
        </ActionIcon>
        <div>
          <Title order={5}>Source field value mapping</Title>
          <Text size="sm" color="dimmed">
            Map each value from the source field to a value on the new field.
          </Text>
        </div>
      </Group>

      <Stack spacing={0} className={classes.table}>
        <Group spacing="xs" noWrap={true} align="center" className={classes.columnHeaders}>
          <Text size="sm" className={classes.sourceHeader}>
            Source field values
          </Text>
          {/* Counter sits above the destination column, matching the design. */}
          <Group spacing="xs" position="apart" align="center" noWrap={true} className={classes.destSelect}>
            <Text size="sm">New {destinationFieldName} values</Text>
            <Text size="sm" color="dimmed" fw={400}>
              {resolvedCount}/{sourceOptions.length} mapped
            </Text>
          </Group>
        </Group>

        {sourceOptions.map((sourceOpt) => {
          const isAcknowledged = currentMappings.has(sourceOpt.id);
          const mappedName = currentMappings.get(sourceOpt.id) ?? null;
          // Explicit "— none —" maps to the sentinel; a real name maps to itself; an unacknowledged
          // option has no value so the Select shows its placeholder.
          const selectValue = isAcknowledged ? (mappedName ?? NONE_OPTION_VALUE) : null;
          return (
            <Group key={sourceOpt.id} spacing="xs" noWrap={true} align="center" className={classes.row}>
              <Text size="sm" truncate={true} className={classes.sourceName} title={sourceOpt.name}>
                {sourceOpt.name}
              </Text>
              <Select
                size="xs"
                aria-label={`Destination option for ${sourceOpt.name} in ${destinationFieldName}`}
                data={destSelectData}
                value={selectValue}
                placeholder="Select an option"
                onChange={(val) => {
                  onMappingChange(sourceOpt.id, val === NONE_OPTION_VALUE ? null : val);
                }}
                className={classes.destSelect}
              />
            </Group>
          );
        })}
      </Stack>
    </Stack>
  );
}
